import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useClipboard,
  VStack,
  HStack,
  Radio,
  RadioGroup,
  Stack,
} from "@chakra-ui/react";
import { createDialogContext, DialogProvider } from "../../../../Common/Dialog";
import { useContext, useEffect, useState } from "react";
import { KYDAdminEvent } from "@common/interfaces/KYDEvent";
import { useForm, Controller } from "react-hook-form";
import { KYDOrganizationEventLink } from "@common/interfaces/KYDOrganization";
import PermissionSelector from "./Permissions";
import PhoneInput from "react-phone-input-2";

export const LinkOrganizationDialogContext = createDialogContext();

const LinkOrganizationDialog = ({
  editingLinkedOrganization,
  isLoading = false,
  onDelete,
  onSave,
  onClose,
}: {
  kydEvent?: KYDAdminEvent;
  isLoading?: boolean;
  editingLinkedOrganization?: KYDOrganizationEventLink;
  onSave?: (values: Partial<KYDOrganizationEventLink>) => Promise<void>;
  onDelete: (item: KYDOrganizationEventLink) => Promise<void>;
  onClose: () => void;
}) => {
  const { isOpen, close } = useContext(LinkOrganizationDialogContext);
  const { onCopy, hasCopied } = useClipboard(
    editingLinkedOrganization?.organization_id
  );

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    reset,
    control,
    formState: { isValid, isDirty },
  } = useForm({
    mode: "all",
    criteriaMode: "all",
    values: {
      ...editingLinkedOrganization,
      organization_id: editingLinkedOrganization
        ? editingLinkedOrganization.organization_id
        : "",
      phone_number: "",
      type: editingLinkedOrganization
        ? editingLinkedOrganization.organization?.type
        : "business",
      scopes: editingLinkedOrganization ? editingLinkedOrganization.scopes : [],
    },
  });

  const scopes = watch("scopes");
  const type = watch("type");

  // Contacts sharing is only available for organization collaborators.
  // If the invite type is switched to "Individual", strip contacts scope to avoid persisting it.
  useEffect(() => {
    if (type !== "personal") {
      return;
    }
    const next_scopes = (scopes || []).filter(
      (s) => s !== "external/contacts:write"
    );
    if (next_scopes.length !== (scopes || []).length) {
      setValue("scopes", next_scopes, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [type, scopes, setValue]);
  const _onSubmit = async (values: Partial<KYDOrganizationEventLink>) => {
    try {
      await onSave(values);
    } catch (e) {}
  };

  const _onClose = () => {
    reset();
    close();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      size="xl"
      isCentered
      onClose={() => {
        _onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader paddingTop={4}>
          <Text w="full">
            {editingLinkedOrganization
              ? `View link with ${editingLinkedOrganization.organization?.name}`
              : "Invite Outside Collaborator"}
          </Text>
        </ModalHeader>
        <ModalBody>
          <VStack w="full" spacing={2}>
            <VStack mb={4} w="full" gap={1}>
              {editingLinkedOrganization ? null : (
                <Stack w="full">
                  <Text fontWeight={"medium"} w="full" textAlign={"left"}>
                    Invitation Type
                  </Text>
                  <Controller
                    name="type"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup value={value} onChange={onChange}>
                        <HStack spacing={4}>
                          <Radio value="business">Organization</Radio>
                          <Radio value="personal">Individual</Radio>
                        </HStack>
                      </RadioGroup>
                    )}
                  />
                </Stack>
              )}
            </VStack>

            <VStack w="full" spacing={2}>
              {editingLinkedOrganization ? (
                <>
                  <Text fontWeight={"medium"} w="full">
                    Organization
                  </Text>
                  <Input
                    value={editingLinkedOrganization.organization?.name}
                    isDisabled={!!editingLinkedOrganization}
                    readOnly
                    placeholder="Organization Name"
                  />
                  <Text fontSize={"xs"} w="full" opacity={0.5} onClick={onCopy}>
                    {editingLinkedOrganization.organization_id}
                  </Text>
                </>
              ) : type === "business" ? (
                <>
                  <Text fontWeight={"medium"} w="full">
                    Organization ID
                  </Text>
                  <Text fontSize={"xs"}>
                    The organization ID can be found by the organization you
                    want to share with in KYD platform {">"} Settings on the
                    left sidebar
                  </Text>
                  <Input
                    {...register("organization_id", {
                      required: type === "business",
                    })}
                    isDisabled={isLoading}
                    placeholder="Organization ID"
                  />
                </>
              ) : (
                <Stack w="full">
                  <Text fontWeight={"medium"} w="full">
                    Phone Number
                  </Text>
                  <Text fontSize={"xs"}>
                    Enter the phone number of the person you want to invite as a
                    collaborator
                  </Text>
                  <Controller
                    name="phone_number"
                    control={control}
                    rules={{ required: type === "personal" }}
                    render={({ field: { onChange, value } }) => (
                      <PhoneInput
                        value={value}
                        onChange={onChange}
                        autoFormat={true}
                        inputProps={{
                          placeholder: "Mobile number",
                        }}
                        inputStyle={{
                          width: "100%",
                          height: "60px",
                          fontFamily: "Work sans",
                          borderWidth: "1px",
                          borderRadius: "5px",
                        }}
                        countryCodeEditable={false}
                        country={"us"}
                        disabled={isLoading}
                      />
                    )}
                  />
                </Stack>
              )}
            </VStack>

            <PermissionSelector
              mode="organization"
              scopes={scopes}
              collaboratorType={type}
              isDisabled={isLoading}
              onChange={(values) =>
                setValue("scopes", values.scopes, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr="4" isDisabled={isLoading} onClick={_onClose}>
            Close
          </Button>
          {editingLinkedOrganization ? (
            <Button
              mr="4"
              bg="red.500"
              color="white"
              isDisabled={isLoading || !isValid}
              isLoading={isLoading}
              onClick={() => {
                onDelete(editingLinkedOrganization);
                _onClose();
              }}
            >
              Delete
            </Button>
          ) : null}
          <Button
            bg="green.500"
            color="white"
            isDisabled={isLoading || !isValid || !isDirty}
            isLoading={isLoading}
            onClick={handleSubmit(_onSubmit)}
          >
            {editingLinkedOrganization ? "Save" : "Invite"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const withLinkOrganizationDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={LinkOrganizationDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default LinkOrganizationDialog;
